"use client";

import { useState, useRef, useEffect, use } from "react";
import { useRouter } from "next/navigation";
import { logger } from "@/lib/logger";
import toast from "react-hot-toast";
import { MessageBubble } from "@/components/MessageBubble";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Send, MoreVertical } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { csrfFetch } from "@/lib/csrf-client";

interface Message {
  id: string;
  content: string;
  isOwn: boolean;
  timestamp: Date;
  status?: "sent" | "delivered" | "read";
}

interface OtherParticipant {
  id: string;
  name: string;
  avatar: string | null;
  type: string;
  isOnline: boolean;
}

interface ConversationData {
  id: string;
  otherParticipant: OtherParticipant;
  messages: Message[];
}

export default function ConversationPage({ params }: { params: Promise<{ conversationId: string }> }) {
  const router = useRouter();
  const resolvedParams = use(params);
  const conversationId = resolvedParams.conversationId;
  
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [otherParticipant, setOtherParticipant] = useState<OtherParticipant | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    let channel: any = null;
    let mounted = true;

    async function setupConversation() {
      await fetchConversation();
      
      // Verify user is authenticated before setting up subscription
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !mounted) return;

      // Subscribe to new messages in this conversation
      channel = supabase
        .channel(`conversation:${conversationId}`)
        .on('postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            if (!mounted) return;
            
            const newMessage = payload.new as { id: string; sender_id: string; content: string; created_at: string; status?: string };
            // Only add if it's not from us (we already added it optimistically)
            if (newMessage.sender_id !== user.id) {
              setMessages(prev => [...prev, {
                id: newMessage.id,
                content: newMessage.content,
                isOwn: false,
                timestamp: new Date(newMessage.created_at),
                status: newMessage.status as "sent" | "delivered" | "read"
              }]);
            }
          }
        )
        .subscribe();
    }

    setupConversation();

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [conversationId, supabase]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversation = async () => {
    try {
      const response = await fetch(`/api/messages/${conversationId}`);
      if (response.ok) {
        const data = await response.json();
        const conversation: ConversationData = data.conversation;
        setOtherParticipant(conversation.otherParticipant);
        setMessages(conversation.messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        })));
      } else if (response.status === 404) {
        toast.error("Conversation not found");
        router.push('/messages');
      } else {
        toast.error("Failed to load conversation");
      }
    } catch (error) {
      logger.error("Error fetching conversation:", error);
      toast.error("Failed to load conversation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !sending) {
      setSending(true);
      const messageContent = message.trim();
      
      // Optimistic update
      const tempId = Date.now().toString();
      const newMessage: Message = {
        id: tempId,
        content: messageContent,
        isOwn: true,
        timestamp: new Date(),
        status: "sent"
      };
      setMessages(prev => [...prev, newMessage]);
      setMessage("");

      try {
        const response = await csrfFetch(`/api/messages/${conversationId}/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: messageContent })
        });

        if (response.ok) {
          const data = await response.json();
          // Update the temp message with the real one
          setMessages(prev => prev.map(msg =>
            msg.id === tempId ? { ...data.message, timestamp: new Date(data.message.timestamp) } : msg
          ));
        } else {
          // Remove the optimistic message on error
          setMessages(prev => prev.filter(msg => msg.id !== tempId));
          setMessage(messageContent); // Restore the message
          toast.error("Failed to send message. Please try again.");
        }
      } catch (error) {
        logger.error("Error sending message:", error);
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
        setMessage(messageContent);
        toast.error("Failed to send message. Please try again.");
      } finally {
        setSending(false);
      }
    }
  };

  const initials = otherParticipant?.name
    ? otherParticipant.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "??";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col pb-20">
        <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
          <div className="mx-auto flex h-14 max-w-screen-sm items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => router.push("/messages")}
                className="h-10 w-10"
                aria-label="Go back to messages"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="animate-pulse flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-muted" />
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-3 w-16 bg-muted rounded" />
                </div>
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading messages...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-screen-sm items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.push("/messages")}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={otherParticipant?.avatar || ""} alt={otherParticipant?.name || ""} />
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="font-semibold text-base">{otherParticipant?.name || "Unknown"}</h2>
                <p className="text-xs text-muted-foreground">
                  {otherParticipant?.isOnline ? "Online" : "Offline"}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-10 w-10" aria-label="More options">
              <MoreVertical className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-screen-sm px-4 py-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-muted-foreground">No messages yet. Say hello!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  content={msg.content}
                  isOwn={msg.isOwn}
                  timestamp={msg.timestamp}
                  status={msg.status}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>

      {/* Message Input */}
      <footer className="sticky bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-w-screen-sm p-4">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <Input
              type="text"
              placeholder="Type a message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="flex-1 h-12 text-base"
              disabled={sending}
            />
            <Button
              type="submit"
              size="icon"
              className="h-12 w-12 shrink-0"
              disabled={!message.trim() || sending}
              aria-label="Send message"
            >
              <Send className="h-5 w-5" />
            </Button>
          </form>
        </div>
      </footer>
    </div>
  );
}
